import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaClock, FaPhone, FaWhatsapp } from 'react-icons/fa';

const Footer = () => {
  const { profile } = useSelector((state) => state.profile);
  const navigate = useNavigate();

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleWhatsApp = () => {
    if (profile?.whatsapp) {
      const message = `Hello! I would like to place an order at ${profile.name}.`;
      const url = `https://wa.me/${profile.whatsapp}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
    }
  };

  const handleCall = () => {
    if (profile?.mobile) {
      window.open(`tel:${profile.mobile}`, '_blank');
    }
  };

  const handlePrivacyPolicy = () => {
    navigate('/privacy-policy');
  };

  const handleTermsOfService = () => {
    navigate('/terms-of-service');
  };

  return (
    <footer className="bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 text-gray-800 pb-20 md:pb-0">
      <div className="max-w-screen-xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          
          {/* Restaurant Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-4">
              {profile?.DocLogo?.imageUrl && (
                <img 
                  src={profile.DocLogo.imageUrl} 
                  alt={profile?.name || 'Restaurant Logo'} 
                  className="w-12 h-12 object-contain rounded-lg mr-4"
                />
              )}
              <h3 className="text-2xl font-bold poppins text-gray-800">
                {profile?.name || 'Restaurant Name'}
              </h3>
            </div>
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              {profile?.description}
            </p>
            <div className="flex items-center text-gray-700 mb-4">
              <FaMapMarkerAlt className="w-5 h-5 mr-3 text-primary" />
              <span className="poppins">{profile?.address || 'Address not available'}</span>
            </div>
            
            {/* Restaurant Image */}
            {profile?.DocImage?.imageUrl && (
              <div className="mb-6 relative group">
                <div className="relative overflow-hidden rounded-2xl shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:scale-105 hover:-rotate-1">
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10 rounded-2xl"></div>
                  
                  {/* Image */}
                  <img 
                    src={profile.DocImage.imageUrl} 
                    alt={profile?.name || 'Restaurant Image'} 
                    className="w-full h-64 md:h-72 object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  
                  {/* Restaurant Name Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                    <h4 className="text-white text-xl md:text-2xl font-bold poppins drop-shadow-lg">
                      {profile?.name || 'Restaurant'}
                    </h4>
                    <p className="text-white/90 text-sm md:text-base poppins drop-shadow-md">
                    Experience freshness, every time you shop
                    </p>
                  </div>
                  
                  {/* Decorative Elements */}
                  <div className="absolute top-4 right-4 w-3 h-3 bg-white/30 rounded-full animate-pulse"></div>
                  <div className="absolute top-8 right-8 w-2 h-2 bg-white/20 rounded-full animate-pulse delay-300"></div>
                  <div className="absolute top-12 right-6 w-1 h-1 bg-white/40 rounded-full animate-pulse delay-700"></div>
                </div>
              </div>
            )}
          </div>

          {/* Opening Hours & Contact Info */}
          <div className="lg:col-span-2 lg:pl-8">
            {/* Opening Hours */}
            <div className="mb-8">
              <h4 className="text-xl font-semibold poppins mb-4 text-gray-800">Opening Hours</h4>
              <div className="space-y-2">
                <div className="flex items-center text-gray-700">
                  <FaClock className="w-4 h-4 mr-3 text-primary" />
                  <span className="poppins">
                    {formatTime(profile?.from_time)} - {formatTime(profile?.to_time)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 poppins ml-7">
                  Open daily for your convenience
                </p>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-xl font-semibold poppins mb-4 text-gray-800">Contact Us</h4>
              <div className="space-y-3">
                <button 
                  onClick={handleCall}
                  className="flex items-center text-gray-700 hover:text-gray-900 transition-colors duration-200 group"
                >
                  <FaPhone className="w-4 h-4 mr-3 text-primary group-hover:scale-110 transition-transform duration-200 transform rotate-90" />
                  <span className="poppins">{profile?.mobile || 'Phone not available'}</span>
                </button>
                <button 
                  onClick={handleWhatsApp}
                  className="flex items-center text-gray-700 hover:text-gray-900 transition-colors duration-200 group"
                >
                  <FaWhatsapp className="w-4 h-4 mr-3 text-green-500 group-hover:scale-110 transition-transform duration-200" />
                  <span className="poppins">WhatsApp</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Our Branches Section - Only show if StoreGroup exists */}
        {profile?.StoreGroup?.BranchList && profile.StoreGroup.BranchList.length > 0 && (() => {
          // Filter out duplicate locations (case-insensitive)
          const uniqueLocations = [];
          const seenLocations = new Set();
          
          profile.StoreGroup.BranchList.forEach((branch) => {
            const locationLower = branch.location?.toLowerCase();
            if (locationLower && !seenLocations.has(locationLower)) {
              seenLocations.add(locationLower);
              uniqueLocations.push(branch);
            }
          });

          return uniqueLocations.length > 0 ? (
            <div className="mt-12 pt-8 border-t border-gray-300">
              <h4 className="text-2xl font-bold poppins mb-6 text-gray-800 text-center">Our Branches</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {uniqueLocations.map((branch, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-white rounded-lg shadow-md border-2 border-transparent"
                  >
                    <FaMapMarkerAlt className="w-4 h-4 text-primary" />
                    <span className="text-gray-800 font-semibold poppins text-sm">
                      {branch.location}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null;
        })()}

        {/* Bottom Bar */}
        <div className="border-t border-gray-300 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-600 poppins text-sm">
              © Yoro Technologies. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <button 
                onClick={handlePrivacyPolicy}
                className="text-gray-600 poppins text-sm hover:text-gray-900 transition-colors duration-200 cursor-pointer"
              >
                Privacy Policy
              </button>
              <button 
                onClick={handleTermsOfService}
                className="text-gray-600 poppins text-sm hover:text-gray-900 transition-colors duration-200 cursor-pointer"
              >
                Terms of Service
              </button>
              <button 
                onClick={() => {
                  const message = "Hello 👋,\nI came across your website and I'm interested in knowing more about your YoroKart.\nCould you please share the details, pricing, and how it works?\n\nThank you 🙏";
                  const url = `https://wa.me/919207600660?text=${encodeURIComponent(message)}`;
                  window.open(url, '_blank');
                }}
                className="text-gray-600 poppins text-sm hover:text-gray-900 transition-colors duration-200 cursor-pointer"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
